import {
  Autocomplete,
  Button,
  FormControlLabel,
  InputAdornment,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Checkbox from "@mui/material/Checkbox";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState, useMemo } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setDevice as setDeviceAction,
  setDisplay,
  setIsInitialized,
  setSelectedInterval,
  setSelectedPage,
  setSelectedPeriod,
  setSelectedPriode,
  setQuarter as setQuarterAction,
} from "../../../../../../features/pageSpeedPerformanceSlice";

interface PerformanceDashboardFilterProps {
  devices: any;
  device: any;
  onDeviceChange: any;
  year: any;
  setYear: any;
  months: any;
  month: any;
  setMonth: any;
  setQuarter: any;
  quarters: any;
  years: any;
  loading: any;
  setShow: any;
}

const PerformanceDashboardFilter = ({
  devices,
  device,
  onDeviceChange,
  year,
  setYear,
  quarters,
  setQuarter,
  month,
  setMonth,
  loading,
  setShow,
}: PerformanceDashboardFilterProps) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const {
    exequteQueryContentQuery4,
    selectedPage,
    selectedPeriod,
    selectedInterval,
    quarter,
    selectedPriode,
  } = useSelector((state: any) => state.pageSpeedPerformance);
  const dispatch = useDispatch();
  const queryOutput = exequteQueryContentQuery4?.payload || [];
  const [searchParams, setSearchParams] = useSearchParams();
  const uniquePageNames = Array.from(
    new Set(queryOutput.map((item: any) => item.page_name))
  );
  const uniquePageUrls = Array.from(
    new Set(queryOutput.map((item: any) => item.page_url))
  );
  const uniquePages = uniquePageNames.map((name) => ({
    label: name,
    type: "name",
  }));
  const uniqueUrls = uniquePageUrls.map((url) => ({
    label: url,
    type: "url",
  }));
  const combinedUniquePages = uniquePages.map((name, index) => ({
    label: `${name.label} : ${uniqueUrls[index]?.label || ""}`,
    page_name: name.label,
    page_url: uniqueUrls[index]?.label || "",
  }));
  const handleShow = () => {
    setShow(true);
    const updatedParams = new URLSearchParams(searchParams.toString());
    updatedParams.set("deviceTab1", device?.label);
    updatedParams.set("intervalTab1", selectedInterval?.value);
    if (quarter !== null) {
      updatedParams.set("quarterTab1", quarter?.label);
    }
    updatedParams.set(
      "selectedPeriodTab1",
      selectedPriode?.format("YYYY-MM-DD")
    );

    setSearchParams(updatedParams);
  };

  const intervalList = useMemo(
    () => [
      { label: "Quarter", value: "quarter" },
      { label: "Month", value: "month" },
    ],
    []
  );

  const handleIntervalChange = (_: unknown, newValue: any) => {
    if (newValue?.value !== selectedInterval?.value) {
      dispatch(setDisplay(false));
    }
    dispatch(setSelectedInterval(newValue));
    if (newValue) {
      if (newValue.value === "quarter") {
        dispatch(setSelectedPeriod("Quarter"));
      } else {
        dispatch(setSelectedPeriod("Month"));
      }
    } else {
      dispatch(setSelectedPeriod(null));
    }
  };

  const minDate = dayjs().subtract(6, "month"); // 6 months back
  const maxDate = dayjs().add(2, "year"); // 2 years ahead

  const handlePeriodChange = (newDate: any) => {
    dispatch(setSelectedPriode(newDate));
    setYear(newDate?.year());
    setMonth({ label: newDate?.format("MMMM") });
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const deviceParam = params.get("deviceTab1");
    if (deviceParam) {
      const foundDevice =
        devices.find((d: any) => d.label === deviceParam) || devices[0];
      dispatch(setDeviceAction(foundDevice));
    }

    const intervalParam = params.get("intervalTab1");
    if (intervalParam) {
      const foundInterval =
        intervalList.find((i: any) => i.value === intervalParam) ||
        intervalList[1];
      dispatch(setSelectedInterval(foundInterval));

      // Set the period based on interval
      if (foundInterval.value === "quarter") {
        dispatch(setSelectedPeriod("Quarter"));
      } else {
        dispatch(setSelectedPeriod("Month"));
      }
    }

    const quarterParam = params.get("quarterTab1");
    if (quarterParam) {
      const foundQuarter = quarters.find((q: any) => q.label === quarterParam);
      if (foundQuarter) {
        dispatch(setQuarterAction(foundQuarter));
        setQuarter(foundQuarter);

        // Extract and set the year from the quarter label (e.g., "Q4 2023")
        const yearFromQuarter = foundQuarter.label.split(" ")[1];
        if (yearFromQuarter) {
          setYear(parseInt(yearFromQuarter));
        }
      }
    }

    const selectedPriodeParam = params.get("selectedPeriodTab1");
    if (selectedPriodeParam) {
      dispatch(setSelectedPriode(dayjs(selectedPriodeParam)));
    }

    dispatch(setIsInitialized(true));
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Grid container spacing={3} mb={3} mt={3}>
        <Grid size={9}>
          <Grid container spacing={2}>
            <Grid size={3}>
              <Autocomplete
                disabled={loading}
                disablePortal
                options={devices}
                value={device}
                getOptionLabel={(option) => option?.label}
                onChange={(_, newValue: any) => {
                  onDeviceChange(newValue);
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Device *" color="secondary" />
                )}
                size="small"
              />
            </Grid>
            <Grid size={3}>
              <Autocomplete
                disabled={loading}
                sx={{ width: "100%" }}
                options={intervalList}
                value={selectedInterval}
                onChange={handleIntervalChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Interval"
                    label="Time Interval *"
                    InputProps={{
                      ...params.InputProps,
                    }}
                    color="secondary"
                  />
                )}
                size="small"
              />
            </Grid>
            <Grid size={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {selectedInterval?.value === "quarter" ? (
                  <Autocomplete
                    disabled={loading}
                    options={quarters}
                    getOptionLabel={(option: any) => option?.label}
                    value={quarter}
                    onChange={(_, newValue: any) => {
                      setQuarter(
                        newValue
                          ? {
                              label: newValue?.label,
                              startMonth: newValue?.startMonth,
                              endMonth: newValue?.endMonth,
                            }
                          : null
                      );

                      if (newValue) {
                        const selectedYear = newValue.label.split(" ")[1];
                        setYear(selectedYear);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Quarter *"
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                ) : (
                  <DatePicker
                    disabled={loading}
                    minDate={minDate}
                    maxDate={maxDate}
                    views={["year", "month"]}
                    slotProps={{
                      textField: {
                        size: "small",
                        color: "secondary",
                        required: true,
                        fullWidth: true,
                      },
                    }}
                    format="MM/YYYY"
                    value={selectedPriode}
                    label="Time Period"
                    onChange={(newValue: any) => {
                      handlePeriodChange(newValue);
                    }}
                  />
                )}
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={3}>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            <Button
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#9C27B0",
              }}
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            >
              Advanced Options
            </Button>
            <Button
              data-tracker="156"
              variant="contained"
              color="secondary"
              disabled={
                !device ||
                !year ||
                !selectedPeriod ||
                (selectedPeriod === "Month" && !month) ||
                (selectedPeriod === "Quarter" && !quarter)
              }
              sx={{ ml: "1rem" }}
              onClick={handleShow}
            >
              display
            </Button>
          </Stack>
        </Grid>
        {showAdvancedOptions && (
          <Grid size={5} sx={{ display: "flex" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="secondary"
                  checked={selectedPage !== null} // Check if a page is selected
                  onChange={(event) => {
                    dispatch(
                      setSelectedPage(
                        event.target.checked ? combinedUniquePages[0] : null
                      )
                    );
                  }}
                />
              }
              label="Page Specific Data"
              sx={{ whiteSpace: "nowrap" }}
            />
            <Autocomplete
              fullWidth
              options={combinedUniquePages}
              value={selectedPage}
              onChange={(_, newValue: any) => {
                dispatch(setSelectedPage(newValue));
              }}
              getOptionLabel={(option: any) => option?.label} // Display the label in the dropdown
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Page Name or URL"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default PerformanceDashboardFilter;
